// =============================================
// CloudflarePage — shell panelu /cloudflare: TopBar + zakladki sterowane
// query paramem ?tab= (useCloudflareTab). Logika zakladek zyje w
// components/cloudflare/*; stan dzielony miedzy zakladkami (konta, cache zon,
// domeny, kreator) jest TUTAJ, bo Radix odmontowuje nieaktywne TabsContent.
// Run kreatora zyje jeszcze wyzej — w transferRunStore (poza React).
// =============================================
import { TopBar } from "@/components/layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Globe, KeyRound, ListTree, Layers, History, ArrowRightLeft, Loader2 } from "lucide-react";
import { AccountsTab } from "@/components/cloudflare/accounts/AccountsTab";
import { HistoryTab } from "@/components/cloudflare/history/HistoryTab";
import { ZonesTab } from "@/components/cloudflare/zones/ZonesTab";
import { ZoneRowActions } from "@/components/cloudflare/zones/ZoneRowActions";
import { TransferWizard } from "@/components/cloudflare/transfer/TransferWizard";
import { CfPagesTab } from "@/components/cloudflare/CfPagesTab";
import { DnsRecordsTab } from "@/components/cloudflare/DnsRecordsTab";
import { useCloudflareAccounts } from "@/hooks/useCloudflareAccounts";
import { useCloudflareTab } from "@/hooks/useCloudflareTab";
import { useAllZones } from "@/hooks/useAllZones";
import { useTransferDomains } from "@/hooks/useTransferDomains";
import { useTransferWizard } from "@/hooks/useTransferWizard";

const CloudflarePage = () => {
  const { accounts, loading, addAccount, deleteAccount, updateAccount } = useCloudflareAccounts();
  // Cache zon ze wszystkich kont — wspolny dla "Domeny w CF" i kreatora.
  const allZones = useAllZones(accounts);
  // Domeny z panelu (gating Sponsorakow + IP hostingu) — kreator + akcje inline.
  const transferDomains = useTransferDomains();
  const wizard = useTransferWizard({ accounts, allZones, transferDomains });
  const [tab, setTab] = useCloudflareTab("przenies");
  const running = wizard.run.status === "running";

  return (
    <>
      <TopBar title="Cloudflare" />
      <div className="flex-1 overflow-auto p-6">
        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)} className="max-w-6xl">
          <TabsList className="mb-6">
            <TabsTrigger value="przenies">
              {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRightLeft className="h-4 w-4 mr-2" />}Przenieś domeny
            </TabsTrigger>
            <TabsTrigger value="domeny"><Globe className="h-4 w-4 mr-2" />Domeny w CF</TabsTrigger>
            <TabsTrigger value="dns"><ListTree className="h-4 w-4 mr-2" />Rekordy DNS</TabsTrigger>
            <TabsTrigger value="pages"><Layers className="h-4 w-4 mr-2" />Pages</TabsTrigger>
            <TabsTrigger value="konta"><KeyRound className="h-4 w-4 mr-2" />Konta</TabsTrigger>
            <TabsTrigger value="historia"><History className="h-4 w-4 mr-2" />Historia</TabsTrigger>
          </TabsList>

          <TabsContent value="przenies">
            <TransferWizard
              accounts={accounts}
              loading={loading}
              allZones={allZones}
              transferDomains={transferDomains}
              wizard={wizard}
              onShowHistory={() => setTab("historia")}
            />
          </TabsContent>
          <TabsContent value="domeny">
            <ZonesTab
              accounts={accounts}
              loading={loading}
              allZones={allZones}
              renderActions={(zone, rec, setRecord) => (
                <ZoneRowActions zone={zone} records={rec} accounts={accounts} findDomain={transferDomains.findDomain} onRecordsChanged={setRecord} />
              )}
            />
          </TabsContent>
          <TabsContent value="dns">
            <DnsRecordsTab accounts={accounts} loading={loading} />
          </TabsContent>
          <TabsContent value="pages">
            <CfPagesTab accounts={accounts} loading={loading} />
          </TabsContent>
          <TabsContent value="konta">
            <AccountsTab accounts={accounts} loading={loading} addAccount={addAccount} deleteAccount={deleteAccount} updateAccount={updateAccount} />
          </TabsContent>
          <TabsContent value="historia">
            <HistoryTab accounts={accounts} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
};

export default CloudflarePage;
